import React, {useState} from 'react';
import {
  Text,
  FormField,
  FormFieldHelperText,
  FormFieldLabel,
  H3,
  Input,
  FlexItem,
  StackLayout,
  RadioButtonGroup,
  RadioButton
} from '@salt-ds/core';
import PageNavigation from '../../components/common/page-navigation/PageNavigation';
import Constants from '../../components/common/Constants';
import {useNavigate} from 'react-router';
import {formatBillingZipCode, formatCardNumber, formatPhoneNumber, isCanadianZipCode} from '../../common/ApiUtils';
import {verifyUserId} from '../../api/OnlineProfileRegisterationApi';

export default function OnlineProfileRegistrationAuthenticateForm(props) {
  const navigate = useNavigate();
  const [cardFormData, setCardFormData] = useState({
    commercialCardNumber: "",
    cardBillingZipCode: "",
    cardBusinessPhoneNumber: ""
  });
  const [formData, setFormData] = useState({userId: "", orgId: ""});
  const [errors, setErrors] = useState({
    commercialCardNumber: false,
    verificationOption: {required: false, invalid: false}
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAuthenticationOption, setSelectedAuthenticationOption] = useState("");

  const handleChange = (event) => {
    let {name, value} = event.target;

    if (name === "commercialCardNumber") {
      let formattedValue = value.replace(/\D/g, '');
      value = formatCardNumber(formattedValue);
    } else if (name === "cardBillingZipCode") {
      value = formatBillingZipCode(value);
    } else if (name === "cardBusinessPhoneNumber") {
      let formattedValue = value.replace(/\D/g, '');
      value = formatPhoneNumber(formattedValue);
    }

    setCardFormData((prevFormData) => ({...prevFormData, [name]: value}));

    //Clear error when user begins to type
    if (name === 'commercialCardNumber') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        commercialCardNumber: false,
        commercialCardNumberInvalid: false,
      }));
    } else if (name === 'cardBillingZipCode' || name === 'cardBusinessPhoneNumber') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        verificationOption: {required: false, invalid: false}
      }));
      if (props.setHasError) props.setHasError(false); // Remove error banner when input changes
    }
  };

  //Validation for empty fields & format
  const validateFormData = (cardFormData) => {
    // Remove non-digit characters for phone validation
    const phoneDigits = cardFormData.cardBusinessPhoneNumber.replace(/\D/g, '');
    const cardDigits = cardFormData.commercialCardNumber.replace(/\D/g, '');
    const isZipSelected = selectedAuthenticationOption === "cardBillingZipCode";
    const isPhoneSelected = selectedAuthenticationOption === "cardBusinessPhoneNumber";

    return {
      commercialCardNumber: cardFormData.commercialCardNumber.trim() === '',
      commercialCardNumberInvalid: cardDigits.length > 0 && cardDigits.length < 16,
      verificationOption: {
        required:
            (!isZipSelected && !isPhoneSelected) ||
            (isZipSelected && cardFormData.cardBillingZipCode.trim() === '') ||
            (isPhoneSelected && cardFormData.cardBusinessPhoneNumber.trim() === ''),
        invalid:
            (isZipSelected &&
                ((isCanadianZipCode(cardFormData.cardBillingZipCode) &&
                        cardFormData.cardBillingZipCode.length !== 7) ||
                    (!isCanadianZipCode(cardFormData.cardBillingZipCode) &&
                        (cardFormData.cardBillingZipCode.length < 5 ||
                            (cardFormData.cardBillingZipCode.length > 5 &&
                                cardFormData.cardBillingZipCode.length < 9))))) ||
            (isPhoneSelected &&
                cardFormData.cardBusinessPhoneNumber.trim() !== '' &&
                phoneDigits.length < 10),
      },
    };
  };

  // const handleResponse = (response) => {
  //   if (response.data) {
  //     storeSessionData(response.data);
  //     navigateBasedOnResponse(response.data);
  //   } else if (response.error && response.error.detail) {
  //     resetFormData();
  //     props.showErrorOnPage(response.error.detail);
  //   }
  // };

  const handleResponse = (response) => {
    if (response.data) {
      // Create the updated data object first
      const updatedFormData = {
        ...formData,
        userId: response.data.userName,
        orgId: response.data.orgNB
      };

      // Update the state
      setFormData(updatedFormData);
      storeSessionData(response.data);
      navigateBasedOnResponse(response.data, updatedFormData);
    } else if (response.error && response.error.detail) {
      resetFormData();
      setFormData(null); // Clear the response data on error
      props.showErrorOnPage(response.error.detail);
    }
  };

  const storeSessionData = (data) => {
    sessionStorage.setItem('userEmail', data.userEmail);
    sessionStorage.setItem('orgName', data.orgNB);
    sessionStorage.setItem('loginName', data.userName);
    sessionStorage.setItem('accountNumber', cardFormData.commercialCardNumber.replaceAll("-", ""));
    if (data.csrf !== null) {
      sessionStorage.setItem(data.csrf.headerName, data.csrf.token);
    }
  };

  const navigateBasedOnResponse = (data, updatedFormData) => {
    if (data.hasOwner === false) {
      navigate(Constants.profileRegistrationCreateUserId, {
        state: {
          from: Constants.onlineProfileRegistrationAuthenticate
        }
      });
    } else if (data.hasOwner === true) {
      navigate(Constants.profileRegistrationCompleted, {
        state: {
          formData: updatedFormData,
          from: Constants.onlineProfileRegistrationAuthenticate
        }
      });
    }
  };

  const resetFormData = () => {
    setCardFormData({commercialCardNumber: "", cardBillingZipCode: "", cardBusinessPhoneNumber: ""});
  };

  const handleSubmit = async (event) => {
    props.setIsLoading(true);
    setIsSubmitting(true);
    event.preventDefault();

    const newErrors = validateFormData(cardFormData);
    setErrors(newErrors);

    // Show error banner only if commercial card number is filled and no radio button is selected
    if (cardFormData.commercialCardNumber.trim() !== '' && !selectedAuthenticationOption) {
      if (props.setHasError) props.setHasError(true);
      if (props.showErrorOnPage) props.showErrorOnPage("Please select an authentication option to continue.");
    } else {
      if (props.setHasError) props.setHasError(false);
    }

    //Submit the form
    if (!newErrors.commercialCardNumber && !newErrors.commercialCardNumberInvalid && !newErrors.verificationOption.required && !newErrors.verificationOption.invalid) {
      try {
        const response = await verifyUserId(cardFormData.commercialCardNumber, cardFormData.cardBillingZipCode, cardFormData.cardBusinessPhoneNumber);
        handleResponse(response);
        setIsSubmitting(false);
      } catch (error) {
        setIsSubmitting(false);
        throw error;
      } finally {
        props.setIsLoading(false);
      }
    } else {
      props.setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const hasVerificationOptionError = () => {
    return errors.verificationOption.required || errors.verificationOption.invalid;
  };

  return (
      <form autoComplete="off" data-testid="online-profile-registration-card-form" onSubmit={handleSubmit}>
        <fieldset disabled={isSubmitting} style={{border:0, padding:0, margin:0}}>
          <StackLayout gap={2}>
            <FormField validationStatus={errors.commercialCardNumber || errors.commercialCardNumberInvalid ? "error" : undefined} className="form-layout">
              <FormFieldLabel htmlFor="commercialCardNumber">{Constants.commercialCardNumberLabel}</FormFieldLabel>
              <Input id="commercialCardNumber"
                     inputProps={{name: "commercialCardNumber", value: cardFormData.commercialCardNumber, "aria-required": "true",
                     "aria-describedby": errors.commercialCardNumber ? "commercialCardNumber-error" : undefined}}
                     onChange={handleChange}
                     required />
              {errors.commercialCardNumber && (
                <FormFieldHelperText id="commercialCardNumber-error" role="alert">
                  {Constants.requiredFieldError}
                </FormFieldHelperText>
              )}
              {errors.commercialCardNumberInvalid && (
                <FormFieldHelperText id="commercialCardNumber-error" role="alert">
                  {Constants.commercialCardNumberHelper}
                </FormFieldHelperText>
              )}
              {!errors.commercialCardNumber && (
                <FormFieldHelperText>
                  {Constants.commercialCardNumberHelper}
                </FormFieldHelperText>
              )}
            </FormField>
            <FlexItem>
              <H3>
                {Constants.onlineProfileRegistrationAdditionalOptionInstructionalText}
              </H3>
              <Text>
                Please choose one.
              </Text>
            </FlexItem>
            <FlexItem>
              <RadioButtonGroup value={selectedAuthenticationOption} direction="vertical"
                                onChange={(e) => {
                                  setSelectedAuthenticationOption(e.target.value);
                                  if (props.setHasError) props.setHasError(false); // Remove error banner when radio selected
                                }}>

                <RadioButton data-testid="billing-zip-code-radio-button" label={Constants.cardBillingZipCodeLabel} value="cardBillingZipCode"
                  onKeyDown={e => {
                    if (e.key === " " || e.key === "Enter") {
                      setSelectedAuthenticationOption("cardBillingZipCode");
                      e.preventDefault();
                    }
                  }}/>
                {selectedAuthenticationOption === "cardBillingZipCode" && (
                    <FormField validationStatus={hasVerificationOptionError() ? "error" : undefined}
                               className="form-layout" style={{marginBottom: "0.5rem"}}>
                      <FormFieldLabel htmlFor="cardBillingZipCode" className="radio-label">{Constants.cardBillingZipCodeLabel}</FormFieldLabel>
                      <Input id="cardBillingZipCode" inputProps={{
                        name: "cardBillingZipCode", value: cardFormData.cardBillingZipCode, maxLength: "9", "aria-required": "true",
                        "aria-describedby": (hasVerificationOptionError()) ? "cardBillingZipCode-error" : "cardBillingZipCode-helper"
                      }}
                             defaultValue={cardFormData.cardBillingZipCode} onChange={handleChange}
                             required />
                      {(hasVerificationOptionError()) ? (
                        <FormFieldHelperText id="cardBillingZipCode-error" role="alert">
                          {errors.verificationOption.required ? Constants.requiredFieldError : Constants.cardBillingZipCodeHelper}
                        </FormFieldHelperText>
                      ) : (
                        <FormFieldHelperText id="cardBillingZipCode-helper">
                          {Constants.cardBillingZipCodeHelper}
                        </FormFieldHelperText>
                      )}
                    </FormField>
                )}

                <RadioButton data-testid="business-phone-number-radio-button" label={Constants.cardBusinessPhoneNumberLabel} value="cardBusinessPhoneNumber"
                  onKeyDown={e => {
                    if (e.key === " " || e.key === "Enter") {
                      setSelectedAuthenticationOption("cardBusinessPhoneNumber");
                      e.preventDefault();
                    }
                  }}/>
                {selectedAuthenticationOption === "cardBusinessPhoneNumber" && (
                    <FormField validationStatus={hasVerificationOptionError() ? "error" : undefined} className="form-layout">
                      <FormFieldLabel htmlFor="cardBusinessPhoneNumber" className="radio-label">{Constants.cardBusinessPhoneNumberLabel}</FormFieldLabel>
                      <Input id="cardBusinessPhoneNumber"
                             inputProps={{name: "cardBusinessPhoneNumber", value: cardFormData.cardBusinessPhoneNumber, "aria-required": "true",
                               "aria-describedby": (hasVerificationOptionError()) ? "cardBusinessPhoneNumber-error" : "cardBusinessPhoneNumber-helper"
                             }}
                             onChange={handleChange}
                             required />
                      {(hasVerificationOptionError()) ? (
                        <FormFieldHelperText id="cardBusinessPhoneNumber-error" role="alert">
                          {errors.verificationOption.required ? Constants.requiredFieldError : Constants.cardBusinessPhoneNumberHelper}
                        </FormFieldHelperText>
                      ) : (
                        <FormFieldHelperText id="cardBusinessPhoneNumber-helper">
                          {Constants.cardBusinessPhoneNumberHelper}
                        </FormFieldHelperText>
                      )}
                    </FormField>
                )}
              </RadioButtonGroup>
            </FlexItem>
          </StackLayout>
          <PageNavigation style={{marginTop: "20%"}} />
        </fieldset>
      </form>
  );
}